import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
// FIX: Use the correct aliased export name 'appInstance' and alias it as 'app'
import { appInstance as app } from '../firebase/firebase';

// FIX: Specify the region ('asia-south1') to match your deployment
const functionsInstance = getFunctions(app, 'asia-south1');

// Define the callable wrappers using the correctly configured instance
const getSubmissionsToGrade = httpsCallable(functionsInstance, 'getSubmissions');
const gradeSubmission = httpsCallable(functionsInstance, 'gradeSubmission');

const GradingInterface = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  // Fetch the list of pending submissions on component mount
  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        setLoading(true);
        setError(null);
        // This function will fetch submissions for this specific examiner
        const result = await getSubmissionsToGrade();
        setSubmissions(result.data.submissions);
      } catch (err) {
        // Log the full error to help debug the CORS/region issue
        console.error("Error fetching submissions:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchSubmissions();
  }, []);

  if (loading) {
    return <div className="text-center p-8">Loading submissions...</div>;
  }

  if (error) {
    return <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
      <strong>Error:</strong> {error}
    </div>;
  }

  return (
    <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
      {/* --- Left Panel: List of Submissions --- */}
      <div className="w-full md:w-1/3">
        <h2 className="text-2xl font-bold mb-4">Pending Submissions ({submissions.length})</h2>
        <div className="space-y-2 max-h-[70vh] overflow-y-auto">
          {submissions.length === 0 && (
            <p className="text-gray-500">No submissions are currently waiting for grading.</p>
          )}
          {submissions.map(sub => (
            <button
              key={sub.id}
              onClick={() => setSelectedSubmission(sub)}
              className={`w-full text-left p-3 rounded-md border ${selectedSubmission?.id === sub.id ? 'bg-blue-100 border-blue-400' : 'bg-white hover:bg-gray-50'}`}
            >
              <div className="font-bold">Submission: {sub.id}</div>
              <div className="text-sm">Student ID: {sub.studentId}</div>
              <div className="text-sm">Exam ID: {sub.examId}</div>
            </button>
          ))}
        </div>
      </div>

      {/* --- Right Panel: Grading View --- */}
      <div className="w-full md:w-2/3">
        {selectedSubmission ? (
          <GradingPanel
            submission={selectedSubmission}
            onGradeComplete={() => {
              // Remove the graded submission from the list
              setSubmissions(submissions.filter(s => s.id !== selectedSubmission.id));
              setSelectedSubmission(null);
            }}
          />
        ) : (
          <div className="p-8 bg-white rounded-lg shadow-md text-center text-gray-500">
            Select a submission from the left to begin grading.
          </div>
        )}
      </div>
    </div>
  );
};

// --- This is the detailed panel for a single submission ---
const GradingPanel = ({ submission, onGradeComplete }) => {
  // State to hold the marks: { questionId: 'q2', marks: 0 }
  const [manualGrades, setManualGrades] = useState([]);
  const [checkedSheetUrl, setCheckedSheetUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filter for only subjective questions that need grading
  const subjectiveAnswers = submission.answers.filter(
    ans => ans.type === 'subjective-online' || ans.type === 'subjective-offline'
  );

  // Initialize the grades state
  useEffect(() => {
    setManualGrades(
      subjectiveAnswers.map(ans => ({
        questionId: ans.questionId,
        marks: 0 // Default marks
      }))
    );
  }, [submission]);

  const handleMarkChange = (questionId, marks) => {
    setManualGrades(
      manualGrades.map(g =>
        g.questionId === questionId ? { ...g, marks: parseInt(marks, 10) || 0 } : g
      )
    );
  };

  const handleSubmitGrade = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = {
        submissionId: submission.id,
        manualGrades: manualGrades,
        checkedSheetUrl: checkedSheetUrl || null,
      };

      await gradeSubmission(data);
      alert('Grade submitted successfully!');
      onGradeComplete(); // Tell the parent to refresh

    } catch (err) {
      console.error("Error submitting grade:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-lg">
      <h3 className="text-xl font-bold mb-4">Grading: {submission.id}</h3>
      <p className="mb-2"><strong>Student:</strong> {submission.studentId}</p>
      <p className="mb-4"><strong>Auto-graded (MCQ) Marks:</strong> {submission.autoGradedMarks}</p>

      <div className="space-y-6">
        {subjectiveAnswers.map((ans, index) => (
          <div key={ans.questionId} className="p-4 border rounded-md bg-gray-50">
            <h4 className="font-bold text-lg mb-2">Subjective Question {index + 1}</h4>
            <p className="text-sm mb-2">(Question ID: {ans.questionId})</p>

            {ans.type === 'subjective-online' && (
              <div className="p-3 border bg-white rounded-md">
                <label className="block text-sm font-bold text-gray-700 mb-1">Student's Answer:</label>
                <p className="text-gray-800 whitespace-pre-wrap">{ans.writtenAnswer || '(No answer provided)'}</p>
              </div>
            )}

            {ans.type === 'subjective-offline' && (
              <div className="p-3 border bg-white rounded-md">
                <label className="block text-sm font-bold text-gray-700 mb-1">Student's Uploaded File:</label>
                {ans.uploadedFileUrl ? (
                  <a href={ans.uploadedFileUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    View/Download Answer Sheet
                  </a>
                ) : (
                  <p className="text-gray-500">(No file uploaded)</p>
                )}
              </div>
            )}

            <div className="mt-4 w-full md:w-1/3">
              <label className="block text-sm font-bold text-gray-700 mb-1">Marks</label>
              <input
                type="number"
                className="w-full px-3 py-2 border rounded-md"
                placeholder="Enter marks"
                onChange={(e) => handleMarkChange(ans.questionId, e.target.value)}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6">
        <label className="block text-sm font-bold text-gray-700 mb-1">
          Upload Checked Sheet URL (Optional)
        </label>
        {/* You would replace this with your <PDFUploadComponent /> */}
        <input
          type="text"
          value={checkedSheetUrl}
          onChange={(e) => setCheckedSheetUrl(e.target.value)}
          placeholder="URL of the checked/annotated PDF"
          className="w-full px-3 py-2 border rounded-md"
        />
      </div>

      {error && (
        <div className="bg-red-100 text-red-700 px-4 py-3 rounded my-4">
          <strong>Error:</strong> {error}
        </div>
      )}

      <button
        onClick={handleSubmitGrade}
        disabled={loading}
        className="w-full mt-6 bg-green-600 text-white font-bold py-3 px-4 rounded-md hover:bg-green-700 disabled:bg-gray-400"
      >
        {loading ? 'Submitting...' : 'Finalize and Submit Grade'}
      </button>
    </div>
  );
};

export default GradingInterface;
